import { Component, computed, ElementRef, model, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import * as _ from "underscore";

import {
  faPrint,
  faRankingStar,
  faPieChart,
  faCheckCircle,
  faCircleMinus,
  faFileExcel,
  faSpinner,
  faCircleExclamation,
  faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';

import { SchoolClass } from '../../../../core/models/school-class.model';
import { District } from '../../../../core/models/district.model';
import { School } from '../../../../core/models/school.model';
import { ManagementUser } from '../../../../core/models/management-user.model';
import { SessionStorageService } from '../../../../core/services/session-storage.service';
import { SubscriptionTypes } from '../../../../core/models/subscription-types.model';
import { StudentSession } from '../../../../core/models/student-session.model';
import { UtilityService } from '../../../../core/services/utility.service';
import { StudentsService } from '../../../../core/services/students.service';
import { ReportsService } from '../../../../core/services/reports.service';
import { PrintService } from '../../../../core/services/print.service';
import { LoadingService } from '../../../../core/services/loading.service';
import { DownloadService } from '../../../../core/services/download.service';
import { Objective } from 'src/app/core/models/objective.model';
import * as Sentry from '@sentry/angular';
import { ScoreCutoffs } from '../../../../core/models/score-cutoffs.model';
import { Student } from 'src/app/core/models/student.model';

import { groupUsageProgressIntroSteps } from './group-usage-progress.intro-steps';
import { groupEngagementIntroSteps } from './group-engagement.intro-steps';
import { canViewCoTeacherFilter, canViewSchoolFilter } from 'src/app/reports/utils';
import { CATEGORY_ICONS } from 'src/app/shared/standards/icons';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { PreviousWeekUsageCategoryPipe } from './previous-week-usage-category.pipe';
import { ColorCategoryComponent } from 'src/app/shared/components/color-category/color-category.component';
import { RefreshButton } from 'src/app/shared/components/refresh-button/refresh-button.component';
import { FormsModule } from '@angular/forms';
import { DecimalPipe, NgClass } from '@angular/common';
import { DisplayGradePipe } from 'src/app/core/pipes/display-grade.pipe';
import { StudentsFilterComponent } from 'src/app/shared/components/students-filter/students-filter.component';
import { SchoolTeacherFilterComponent } from 'src/app/shared/components/school-teacher-filter/school-teacher-filter.component';
import { PreviousWeekLevelCategoryPipe } from './previous-week-level-category.pipe';
import { TooltipComponent } from 'src/app/shared/components/tooltip/tooltip.component';
import { TourComponent } from '../../../../shared/components/tour/tour.component';

@Component({
  selector: 'wf-group-summary',
  templateUrl: './group.component.html',
  styleUrls: ['./group.component.css'],
  standalone: true,
  imports: [
    FontAwesomeModule,
    ColorCategoryComponent,
    PreviousWeekUsageCategoryPipe,
    PreviousWeekLevelCategoryPipe,
    RefreshButton,
    FormsModule,
    DecimalPipe,
    DisplayGradePipe,
    NgClass,
    StudentsFilterComponent,
    SchoolTeacherFilterComponent,
    TooltipComponent,
    TourComponent,
  ],
})
export class GroupSummaryComponent implements OnInit {
  currentUser: ManagementUser | null = null ;
  showSchoolFilter: boolean = false ;
  showCoTeacherFilter: boolean = false;
  reportError: string = '' ;
  schoolFilterError: string = '' ;
  hideSharedStudents: boolean = false;
  districts: District[] = [] ;
  schools: School[] = [] ;
  teachers: ManagementUser[] = [] ;
  sessions: StudentSession[] = [] ;
  reportType = model<'usage' | 'engagement'>('usage') ;
  reportTypeDisplay: string = 'Usage and Progress' ;
  selectedZone: string = 'all' ;
  baseFilteredSessions: StudentSession[] = [] ;

  tourSteps = computed(() => {
    const type = this.reportType();
    if (type === 'usage') {
      return groupUsageProgressIntroSteps;
    } else {
      return groupEngagementIntroSteps;
    }
  });

  usageColumns: { [key: string]: string }[] = [
    {
      title: 'Student Name',
      property: 'student.lastName'
    },
    {
      title: 'Grade',
      property: 'student.grade'
    },
    {
      title: 'Teacher',
      property: 'student.teacher'
    },
    {
      title: 'Date Started',
      property: 'startDate'
    },
    {
      title: 'Last Usage Date',
      property: 'lastUsageDate'
    },
    {
      title: 'Current Placement',
      property: 'currentUnit'
    },
    {
      title: 'Progress',
      property: 'currentLevel'
    },
    {
      title: 'Current Zone',
      property: 'zone'
    },
    {
      title: 'Avg Weekly Usage (min)',
      property: 'avgWeeklyUsage'
    },
    {
      title: 'Previous Week Usage (min)',
      property: 'prevUsageRangeTotalTime'
    },
    {
      title: 'Current Week Usage (min)',
      property: 'usageRangeTotalTime'
    },
  ] ;
  engagementColumns: { [key: string]: string }[] = [
    {
      title: 'Student Name',
      property: 'student.lastName'
    },
    {
      title: 'Grade',
      property: 'student.grade'
    },
    {
      title: 'Current Team',
      property: 'student.currentTeam'
    },
    {
      title: 'Total Points',
      property: 'totalPoints'
    },
    {
      title: 'Previous Week Points',
      property: 'previousWeeksPoints'
    },
    {
      title: 'Current Level',
      property: 'currentLevel'
    },
    {
      title: 'Current Week Levels Completed',
      property: 'currentWeekLevelsCompleted'
    },
    {
      title: 'Previous Week Levels Completed',
      property: 'previousWeekLevelsCompleted'
    },
  ] ;
  selectedColumns: { [key: string]: string }[] = [] ;
  filteredSessions: StudentSession[] = [] ;
  grades: string[] = [] ;
  classes: SchoolClass[] = [] ;
  sortColumn: string = '' ;
  sortReverse: boolean = false ;
  fullProduct: string = SubscriptionTypes.FullProduct ;
  exportingReport: boolean = false ;
  reportsIcon = CATEGORY_ICONS['class-reports'];
  printIcon = faPrint ;
  summaryIcon = faRankingStar ;
  loadingIcon = faSpinner ;
  goalMet = faCheckCircle ;
  goalNotMet = faCircleMinus ;
  excelIcon = faFileExcel ;
  errorIcon = faCircleExclamation ;
  showTeacherColumn: boolean = false ;
  helpIcon = faQuestionCircle;

  @ViewChild('printContent') printContent!: ElementRef<HTMLElement>;

  constructor(
    private route: ActivatedRoute,
    private studentService: StudentsService,
    private reportService: ReportsService,
    private sessionStorageService: SessionStorageService,
    private utilityService: UtilityService,
    private printService: PrintService,
    private loadingService: LoadingService,
    private downloadService: DownloadService,
  ) { }

  // FIXME: Sometimes the Grade and Extra Support filters are not set (usually when a school user changes schools)
  ngOnInit(): void {
    let resolvedData = this.route.snapshot.data['resolveData'] ;
    this.currentUser = this.sessionStorageService.userData() ;
    this.showSchoolFilter = canViewSchoolFilter(this.currentUser);
    this.showCoTeacherFilter = canViewCoTeacherFilter(this.currentUser);

    if (resolvedData.erred)
    {
      this.schoolFilterError = resolvedData.message ;
    }
    else
    {
      // grade and class lists might be reduced after filtering co-access students
      const coAccessFilteredData = this.getDropdownListsAfterFilter(resolvedData) ;

      if (this.currentUser!.isTeacherUser())
      {
        this.filteredSessions = coAccessFilteredData.filteredSessions;
        this.sessions = coAccessFilteredData.sessionData;
      } else {
        const selectedTeacherId = this.reportService.getSelectedTeacherForReports().userID;
        this.filteredSessions = selectedTeacherId !== 0
          ? coAccessFilteredData.sessionData.filter((session: StudentSession) =>
          session.student.teacherID === this.reportService.getSelectedTeacherForReports().userID)
          : coAccessFilteredData.sessionData;
        this.sessions = coAccessFilteredData.sessionData ;
      }

      this.baseFilteredSessions = this.sessions;
      this.grades = coAccessFilteredData.grades ;
      this.classes = coAccessFilteredData.classes ;
    }

    // Filter data
    this.selectedColumns = this.usageColumns ;
    this.districts = this.route.snapshot.data['filterData'].districts ;
    this.schools = this.route.snapshot.data['filterData'].schools ;
    this.teachers = this.route.snapshot.data['filterData'].teachers ;

    if (this.currentUser!.isFILUser() || this.currentUser!.isDistrictUser())
    {
      // The FIL user and District user roles can select all schools
      this.schools.unshift({ schoolID: 0, districtID: 0, name: 'All', enabled: true }) ;
    }

    if (canViewSchoolFilter(this.currentUser))
    {
      // The FIL user and District and School user roles can select all teachers
      this.teachers.unshift(ManagementUser.getGenericUser()) ;
    }
  }

  filterSessions(filterOpts: any) {
    const tempFiltered = this.studentService.filterStudentSessions(this.sessions, filterOpts);
    this.baseFilteredSessions = this.filterCoAccessStudents(tempFiltered);
    this.applyZoneFilter();
  }

  filterSessionsByZone() {
    this.applyZoneFilter() ;
  }

  private applyZoneFilter() {
    if (this.selectedZone === 'all') {
      this.filteredSessions = this.baseFilteredSessions ;
      return ;
    }

    this.filteredSessions = this.baseFilteredSessions.filter((session) => {
      const zoneDisplay = this.getZoneDisplay(session) ;

      if (this.selectedZone === 'no-data') {
        return zoneDisplay === '—' ;
      }

      const expectedZone = `Zone ${this.selectedZone}` ;
      return zoneDisplay === expectedZone ;
    }) ;
  }

  private getZoneNumber(session: StudentSession): number | null {
    if (session.latestAssessmentScore && session.latestAssessmentScore.systemScore) {
      const studentGroup = (session.student.grade <= 5) ? 'elementary' : 'secondary';
      const zones = ScoreCutoffs[studentGroup]['zones'];

      for (let idx = 0; idx < zones.length; idx++) {
        const zone = zones[idx];
        if (session.latestAssessmentScore.systemScore >= zone.bottom &&
            session.latestAssessmentScore.systemScore <= zone.top) {
          return idx + 1;
        }
      }
    }

    return null;
  }

  filterSchoolTeacher() {
    this.loadingService.start() ;
    this.reportService.getGroupSummaryReportData().subscribe({
      next: (data: any) => {
        this.loadingService.finish() ;

        if (data.erred)
        {
          this.schoolFilterError = data.message ;
        }
        else
        {
          // grade and class lists might be reduced after filtering co-access students
          const coAccessFilteredData = this.getDropdownListsAfterFilter(data) ;

          this.schoolFilterError = '' ;
          this.sessions = coAccessFilteredData.sessionData ;
          this.baseFilteredSessions = coAccessFilteredData.sessionData;
          this.filteredSessions = coAccessFilteredData.filteredSessions ;
          this.grades = coAccessFilteredData.grades ;
          this.classes = coAccessFilteredData.classes ;
          this.selectedZone = 'all' ;
        }
      },
      error: (err: any) => {
        Sentry.captureException(err, {
          tags: {
            section: 'reports',
            report: 'system-summary-group',
          }
        }) ;
      }
    }) ;
  }

  displayCurrentObjectives(objectives: Objective[]){
    // Since there is no direct way to determine the current objective they're at, we check for objective completion then
    // pick a task and see which category is not completed
    let currentObjectives = '';
    Object.values(objectives).forEach(function(objective) {
      if (!objective.complete)
      {
        let aTask:any = Object.values(objective.taskData)[0];
        let suffix:string = '';
        if (!aTask) return ;

        if (aTask.pretest.score === 0){
          suffix = 'Pre'
        }

        // Note: this was previously commented out, however, we don't seem to be using this function anywhere.
        // TODO: figure out if we need this
        if (aTask.posttest.score != 0){
          suffix = 'Post'
        }

        currentObjectives += 'Objective ' + objective.objectiveNumber + ' ' + suffix;
      }
    });

    return currentObjectives;
  }

  getDisplayValue(session: StudentSession, value: any) {
    const daysBetweenLastUsage = session.lastUsageDate ? Math.floor(((new Date()).getTime() - (new Date(session.lastUsageDate)).getTime()) / (1000 * 3600 * 24)) : 0 ;
    return (session.isCompleted && daysBetweenLastUsage > 6) ? '—' : value;
  }

  getUnitStatus(session: StudentSession) {
    if (session.isCompleted) {
      return 'Completed';
    } else if (session.isInPreDiagnostic) {
      return 'Pre Diagnostic' ;
    } else if (session.isInMidDiagnostic) {
      return 'Mid Diagnostic' ;
    } else if (session.currentUnit === 0 && session.completedPreDiagnostic) {
      return 'Unit 1' ;
    } else if (session.currentUnit === 0) {
      return 'Not Started';
    } else if (session.currentUnit === 24 && session.currentLevel === 120) {
      return 'Post Diagnostic' ;
    } else {
      return 'Unit ' + session.currentUnit;
    }
  }

  getZoneDisplay(session: StudentSession) {
    const studentGroup = (session.student.grade <= 5) ? 'elementary' : 'secondary';
    const zones = ScoreCutoffs[studentGroup]['zones'];
    const zoneIdx = this.getZoneIdxFromSession(session) ;
    return zones[zoneIdx]?.label ?? '—' ;
  }

  filterCoAccessStudents(newData: StudentSession[]) {
    // If 'All Teachers' is selected, then show all students
    const selectedTeacherId = this.reportService.getSelectedTeacherForReports().userID;
    if (this.hideSharedStudents && this.showCoTeacherFilter && selectedTeacherId !== 0) {
      return newData.filter(session => session.student.teacherID === this.reportService.getSelectedTeacherForReports().userID) ;
    } else if (!this.currentUser?.isTeacherUser() && selectedTeacherId !== 0) {
      return newData.filter(session => session.student.teacherID === this.reportService.getSelectedTeacherForReports().userID) ;
    } else {
      return newData
    }
  }

  getDropdownListsAfterFilter(data: any) {
    const filteredSessions = this.filterCoAccessStudents(data.sessionData) ;

    let classes: SchoolClass[] = [] ;
    let grades: string[] = [] ;
    let gradesSet: Set<string> = new Set<string>() ;

    let currentStudents: Student[] = [] ;

    filteredSessions.forEach((session: StudentSession) => {
      gradesSet.add(this.utilityService.getStudentGrade(session.student.grade)) ;
      currentStudents.push(session.student) ;
    }) ;
    grades = [ 'All' ].concat(this.utilityService.sortGrades(Array.from(gradesSet))) ;

    classes = [ {
      name: 'All',
      schoolClassID: 0,
      teacherID: 0,
      createDate: (new Date()),
      status: ''
    }].concat(this.utilityService.sortClasses(this.utilityService.getUniqueSchoolClasses(currentStudents))) ;

    return {
      sessionData: filteredSessions,
      filteredSessions: filteredSessions,
      grades: grades,
      classes: classes,
    }
  }

  formatDate(dateString: string) {
    return this.utilityService.formatDate(dateString) ;
  }

  sortTable(col: string) {
    if (this.sortColumn === col)
    {
      this.sortReverse = !this.sortReverse ;
    }
    this.sortColumn = col ;

    this.filteredSessions = (this.sortReverse) ? _.sortBy(this.filteredSessions, this.sortFunction, this).reverse() : _.sortBy(this.filteredSessions, this.sortFunction, this) ;
  }

  setReportType() {
    if (this.reportType() === 'usage')
    {
      this.selectedColumns = this.usageColumns ;
      this.reportTypeDisplay = 'Usage and Progress' ;
    }
    else
    {
      this.selectedColumns = this.engagementColumns ;
      this.reportTypeDisplay = 'Engagement' ;
    }
  }

  setShowTeacherColumn() {
    this.showTeacherColumn = !this.showTeacherColumn ;
  }

  printSummary() {
    let school = this.reportService.getSelectedSchoolForReports().name;
    let teacher = this.reportService.getSelectedTeacherForReports();
    let teacherName = `${teacher.firstName} ${teacher.lastName}`;
    this.printService.openPrintWindow(this.printContent.nativeElement, school, teacherName);
  }

  toggleSharedStudents() {
    this.refreshTableContent() ;
  }

  refreshTableContent(){
    this.loadingService.start();
    this.reportService.getGroupSummaryReportData().subscribe({
      next: (data: any) => {
        this.loadingService.finish() ;

        if (data.erred)
        {
          this.schoolFilterError = data.message ;
        }
        else
        {
          // grade and class lists might be reduced after filtering co-access students
          const coAccessFilteredData = this.getDropdownListsAfterFilter(data) ;

          this.schoolFilterError = '' ;
          this.sessions = coAccessFilteredData.sessionData ;
          this.baseFilteredSessions = coAccessFilteredData.sessionData ;
          this.filteredSessions = coAccessFilteredData.filteredSessions ;
          this.grades = coAccessFilteredData.grades ;
          this.classes = coAccessFilteredData.classes ;
          this.selectedZone = 'all' ;
        }
      }
    }) ;
  }

  exportGroupSummary() {
    this.reportError = '' ;
    this.exportingReport = true ;
    this.reportService.downloadGroupSummary(this.reportService.getSelectedSchoolForReports().schoolID, this.reportService.getSelectedTeacherForReports().userID, this.showTeacherColumn)
      .subscribe({
        next: (blob: Blob) => {
          const a = document.createElement('a')
          const objectUrl = URL.createObjectURL(blob)
          a.href = objectUrl
          a.download = 'group_summary.xlsx';
          a.click();
          URL.revokeObjectURL(objectUrl);
          this.exportingReport = false ;
        },
        error: (err: any) => {
          this.exportingReport = false ;
          this.reportError = `There was an error trying to export the Group Summary report. Please contact support for assistance.` ;

          Sentry.captureException(err, {
            tags: {
              section: 'reports',
              report: 'system-summary-group',
              action: 'export',
              districtId: this.reportService.getSelectedDistrictForReports().districtID,
              schoolId: this.reportService.getSelectedSchoolForReports().schoolID,
              teacherId: this.reportService.getSelectedTeacherForReports().userID,
            }
          }) ;
        }
      }) ;
  }

  private sortFunction(val: any) {
    if (this.sortColumn === 'student.lastName')
    {
      return val.student.lastName ;
    }

    if (this.sortColumn === 'student.grade')
    {
      return val.student.grade ;
    }

    if (this.sortColumn === 'student.avatarsEarned')
    {
      return val.student.avatarsEarned ;
    }

    if (this.sortColumn === 'student.currentTeam')
    {
      return val.student.currentTeam ;
    }

    if (this.sortColumn === 'student.teacher')
    {
      return val.student.teacher ;
    }

    if (this.sortColumn === 'currentUnit')
    {
      // NOTE: In our currentUnit sorting, we have to manually adjust and set some values
      //     : based on placement to ensure a proper order where diagnostics appear in the right
      //     : temporal order. In particular, a currentUnit of 0 is valid, but our sorting could
      //     : make that placement appear mixed with pre-diagnostic sessions. To avoid this, we
      //     : always add 1 to the unit number if that is our deciding sort param
      //     : (and 2 if we are after the mid -- to make space for the mid). This appears a bit
      //     : confusing, but is needed to maintain the proper chronological order of units.
      if (!val.completedPreDiagnostic && !val.isInPreDiagnostic) {
        // Not started first
        return -1 ;
      }

      if (val.isInPreDiagnostic) {
        // Pre Diagnostic second
        return 0 ;
      }

      if (val.isInMidDiagnostic) {
        // Mid Diagnostic after unit 12 (+1 for our unit 0 case)
        return 14 ;
      }

      if (val.isCompleted)
      {
        // Make completed sessions show up at the end
        return 100 ;
      }

      if (val.currentUnit === 24 && val.currentLevel === 120)
      {
        // Post Diagnostic penultimate
        return 99 ;
      }

      if (val.currentUnit >= 13)
      {
        return val.currentUnit + 2 ;
      }

      return val.currentUnit + 1;
    }

    if (this.sortColumn === 'zone')
    {
      // Sort by zone calculated from latestAssessmentScore.systemScore
      return this.getZoneIdxFromSession(val) ;
    }

    return val[this.sortColumn] ;
  }

  private getZoneIdxFromSession(session: StudentSession): number {
    if (session.latestAssessmentScore && session.latestAssessmentScore.systemScore)
    {
      const studentGroup = (session.student.grade <= 5) ? 'elementary' : 'secondary';
      const zones = ScoreCutoffs[studentGroup]['zones'];

      // First check to make sure our student has completed the pre-diag, the student is indicated to be
      // still in the pre-diag if their latest assessment has a zone of 0
      if (session.latestAssessmentScore.zone === 0) {
        return -1 ;
      }

      const systemScore = Math.floor(session.latestAssessmentScore!.systemScore ?? 0) ;
      return zones.findIndex((zone: any, idx: number) => {
        return (
          (systemScore >= zone.bottom && systemScore <= zone.top) || (idx === 0 && systemScore < zone.bottom)
        ) ;
      });
    }

    return -1 ;
  }
}
